import { ReactNode } from 'react';
import { Reveal } from './Reveal';

/**
 * The heading group every section opens with: a small icon and eyebrow, the
 * gradient-filled heading, and optional supporting copy.
 *
 * The eyebrow is split into a bright lead and a dim trail ("Architecture" /
 * "Features"). That split is editorial rather than decorative — the lead names
 * the section and the trail qualifies it — so it lives in the content module
 * as two fields rather than being inferred from the string here.
 */

interface SectionIntroProps {
  icon: ReactNode;
  eyebrow: { lead: string; trail: string };
  heading: string;
  body?: string;
  /** Centre the group. Used by the sections that have no facing column. */
  align?: 'left' | 'center';
  /** Rendered beside the heading — a CTA on the sections that carry one. */
  aside?: ReactNode;
}

export function SectionIntro({
  icon,
  eyebrow,
  heading,
  body,
  align = 'left',
  aside,
}: SectionIntroProps) {
  const centered = align === 'center';

  return (
    <div
      className={
        centered
          ? 'flex flex-col items-center text-center'
          : 'flex flex-col gap-8 md:flex-row md:items-end md:justify-between'
      }
    >
      {/* Eyebrow, heading and body rise in that order, so the eye is led down
          the group rather than having the whole block appear at once. */}
      <div className={centered ? 'flex flex-col items-center' : 'max-w-3xl'}>
        <Reveal>
          <p className="fias-eyebrow">
            {icon}
            <span>
              {eyebrow.lead}
              <span className="fias-eyebrow-trail">{eyebrow.trail}</span>
            </span>
          </p>
        </Reveal>

        <Reveal delay={90}>
          <h2
            className={`fias-display mt-4 text-[clamp(2rem,4.2vw,2.75rem)] font-semibold leading-[1.2] ${
              centered ? 'max-w-2xl' : ''
            }`}
          >
            {heading}
          </h2>
        </Reveal>

        {body ? (
          <Reveal delay={180}>
            <p className="mt-5 max-w-xl font-sans text-base leading-relaxed tracking-[-0.02em] text-[color:var(--fias-muted)]">
              {body}
            </p>
          </Reveal>
        ) : null}
      </div>

      {aside ? (
        <Reveal delay={240} className="shrink-0">
          {aside}
        </Reveal>
      ) : null}
    </div>
  );
}
